import React from 'react';
import { Agent, NexusAgent } from '../../../domain/agents/models/Agent';
import { ChatProviderType } from '../../../domain/chat/models/ChatProviderType';
import { setSelectedAgents, toggleSelectedAgent } from '../../../redux/features/chatUi/chatUiSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';

export interface KnowledgeSourceAgentOption {
    agentId: string;
    name: string;
    description: string;
    shortDescription?: string;
}

export interface UseKnowledgeSourcesVm {
    isKnowledgeSourcesOpen: boolean;
    knowledgeSourceAgents: KnowledgeSourceAgentOption[];
    selectedKnowledgeSourceIds: string[];
    areAllKnowledgeSourcesSelected: boolean;
    onToggleKnowledgeSourcesOpen: () => void;
    onToggleKnowledgeSource: (agentId: string) => void;
    onToggleAllKnowledgeSources: () => void;
    onCloseKnowledgeSources: () => void;
}

const isKnowledgeSourceAgent = (agent: Agent): agent is NexusAgent => {
    return agent.providerType === ChatProviderType.Nexus && ((agent as NexusAgent).enableFileSearch === true || (agent as NexusAgent).name === 'WebSearchAgent');
};

interface UseKnowledgeSourcesProps {
    sessionId: string | null;
    agentsById: Record<string, Agent>;
    canShowKnowledgeSources: boolean;
}

export const useKnowledgeSources = ({
    sessionId,
    agentsById,
    canShowKnowledgeSources,
}: UseKnowledgeSourcesProps): UseKnowledgeSourcesVm => {
    const dispatch = useAppDispatch();
    const storedSelectedKnowledgeSourceIds = useAppSelector((state) =>
        sessionId ? state.chatUi.bySessionId[sessionId]?.selectedAgents : undefined,
    );
    const [isKnowledgeSourcesOpen, setIsKnowledgeSourcesOpen] = React.useState(false);

    const knowledgeSourceAgents = React.useMemo<KnowledgeSourceAgentOption[]>(
        () =>
            Object.values(agentsById)
                .filter(isKnowledgeSourceAgent)
                .map((agent) => ({
                    agentId: agent.agentId,
                    name: agent.name,
                    description: agent.description,
                    shortDescription: agent.shortDescription,
                })),
        [agentsById],
    );

    const defaultSelectedKnowledgeSourceIds = React.useMemo(
        () => knowledgeSourceAgents.map((agent) => agent.agentId),
        [knowledgeSourceAgents],
    );

    const selectedKnowledgeSourceIds = storedSelectedKnowledgeSourceIds ?? defaultSelectedKnowledgeSourceIds;
    const areAllKnowledgeSourcesSelected =
        knowledgeSourceAgents.length > 0 && selectedKnowledgeSourceIds.length === knowledgeSourceAgents.length;

    React.useEffect(() => {
        if (!canShowKnowledgeSources) {
            setIsKnowledgeSourcesOpen(false);
        }
    }, [canShowKnowledgeSources]);

    React.useEffect(() => {
        if (!sessionId || !canShowKnowledgeSources || storedSelectedKnowledgeSourceIds !== undefined) {
            return;
        }

        dispatch(
            setSelectedAgents({
                sessionId,
                agentIds: defaultSelectedKnowledgeSourceIds,
            }),
        );
    }, [canShowKnowledgeSources, defaultSelectedKnowledgeSourceIds, dispatch, sessionId, storedSelectedKnowledgeSourceIds]);

    const onToggleKnowledgeSourcesOpen = React.useCallback((): void => {
        if (!canShowKnowledgeSources) {
            return;
        }

        setIsKnowledgeSourcesOpen((prev) => !prev);
    }, [canShowKnowledgeSources]);

    const onCloseKnowledgeSources = React.useCallback((): void => {
        setIsKnowledgeSourcesOpen(false);
    }, []);

    const onToggleKnowledgeSource = React.useCallback(
        (agentId: string): void => {
            if (!sessionId) {
                return;
            }

            dispatch(toggleSelectedAgent({ sessionId, agentId }));
        },
        [dispatch, sessionId],
    );

    const onToggleAllKnowledgeSources = React.useCallback((): void => {
        if (!sessionId) {
            return;
        }

        dispatch(
            setSelectedAgents({
                sessionId,
                agentIds: areAllKnowledgeSourcesSelected ? [] : defaultSelectedKnowledgeSourceIds,
            }),
        );
    }, [areAllKnowledgeSourcesSelected, defaultSelectedKnowledgeSourceIds, dispatch, sessionId]);

    return {
        isKnowledgeSourcesOpen,
        knowledgeSourceAgents,
        selectedKnowledgeSourceIds,
        areAllKnowledgeSourcesSelected,
        onToggleKnowledgeSourcesOpen,
        onToggleKnowledgeSource,
        onToggleAllKnowledgeSources,
        onCloseKnowledgeSources,
    };
};

export default useKnowledgeSources;
